import {z} from 'zod';

import {CompanyNameStyleEnum} from '../../../page/main/reviews-generator/pages/questionnare/form/questionnaire-settings/multisurvey-settings/multisurvey-settings-helper';
import {generateResponseSchema} from '../../api/api-type';

export enum QuestionnaireListStatusEnum {
    Ready = 'ready',
    Released = 'released',
}

const questionnaireListItemSchema = z.object({
    id: z.number(),
    answersCount: z.number().nullish(),
    code: z.string(),
    name: z.string(),
    status: z.nativeEnum(QuestionnaireListStatusEnum),
    type: z.union([z.literal('rating'), z.literal('custom')]),
});

export type QuestionnaireListItemType = z.infer<typeof questionnaireListItemSchema>;
export const questionnaireListSchema = generateResponseSchema(questionnaireListItemSchema);

export type QuestionnaireListType = z.infer<typeof questionnaireListSchema>;

export enum SourceItemTypeEnum {
    Catalog = 'catalog',
    CustomLink = 'custom_link',
}

const catalogSourceItemSchema = z.object({
    type: z.literal(SourceItemTypeEnum.Catalog),
    ordering: z.number(),
    catalogId: z.number(),
});

const customLinkSourceItemSchema = z.object({
    type: z.literal(SourceItemTypeEnum.CustomLink),
    ordering: z.number(),
    linkUrl: z.string(),
    linkText: z.string(),
    logoId: z.string().nullable(),
    logoUrl: z.string().nullable(),
    catalogId: z.string().nullable(),
});

const sourceItemSchema = z.union([catalogSourceItemSchema, customLinkSourceItemSchema]);

export type QuestionnaireSourceItemType = z.infer<typeof sourceItemSchema>;

export enum ReviewGeneratorPromoCodeTypeEnum {
    Positive = 'positive',
    Negative = 'negative',
    All = 'all',
}

export enum QuestionnaireStatusEnum {
    Ready = 'ready',
    Temporary = 'temporary',
}

export enum ReviewGeneratorRatingScaleTypeEnum {
    tenPoint = 'ten_point',
    fivePoint = 'five_point',
    twoPoint = 'two_point',
}

export enum ReviewGeneratorQuestionnaireFormFieldsEnum {
    id = 'id',
    name = 'name',
    brand = 'brandId',
    customBrandName = 'customBrandName',
    catalogs = 'catalogLinks',
    negativeCatalogs = 'negativeCatalogLinks',
    negativeReviewMode = 'negativeReviewMode',

    allowSocialMediaLinks = 'allowSocialMediaLinks',
    socialsIds = 'socialsIds',
    socialMediaCallToAction = 'socialsText',

    skipQuestionnaireRating = 'skipInitAssessment',
    questionText = 'questionText',
    ratingText = 'ratingText',
    positiveReaction = 'positiveReactionText',
    negativeReaction = 'negativeReactionText',
    thanksToNegative = 'thanksNegativeReactionText',
    thanksToPositive = 'thanksPositiveReactionText',
    thresholdOfPositivity = 'thresholdOfPositivity',
    emailsForNegative = 'emailForNegativeReviews',
    notificationSettings = 'notificationSettings',
    telegramForNegative = 'tgNotifications',
    tgChatToken = 'tgChatToken',
    termsAndConditionsLanguage = 'languagePrivacy',
    negativeReactionFormSettings = 'negativeReactionFormSettings',
    positiveRatingButtonName = 'positiveRatingButtonName',
    negativeRatingButtonName = 'negativeRatingButtonName',
    ratingScaleType = 'ratingScaleType',
    customRatingButtonName = 'customRatingButtonName',
    canSendPromoCodeOnReview = 'canSendPromoCodeOnReview',
    promoCodeId = 'promoAction',
    promoReviewType = 'promoType',
    promoText = 'promoText',
    negativePromoText = 'negativePromoText',
    promoTextThanks = 'promoTextThanks',
    negativePromoTextThanks = 'negativePromoTextThanks',
    promoCodeWithProcessing = 'promoCodeWithProcessing',
    promoCodeWithProcessingNegative = 'promoCodeWithProcessingNegative',
    logo = 'logo',
    color = 'color',
    logoHeight = 'logoHeight',
    brandTemplateSettings = 'brandTemplateSettings',
    shouldSaveTemplateSettings = 'shouldSaveTemplateSettings',
    mailingSubscriptionSettings = 'mailingSubscriptionSettings',
    directRedirect = 'directRedirect',
    displayAddress = 'displayAddress',
    multiSurveyTitle = 'multiSurveyTitle',
    multiSurveySearchPlaceholder = 'multiSurveySearchPlaceholder',
    multiSurveyButton = 'multiSurveyButton',
    multiSurveyCompanyNameDisplay = 'multiSurveyCompanyNameDisplay',
    autoTagOption = 'allowReviewMapping',
    textSettingsCollapseItems = 'textSettingsCollapseItems',
    multiSurveyCompanyCommentDisplay = 'multiSurveyCompanyCommentDisplay',
    buttonTextColor = 'buttonTextColor',
}

export const reviewGeneratorQuestionnaireFormSchema = z.object({
    [ReviewGeneratorQuestionnaireFormFieldsEnum.id]: z.number().optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.name]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.brand]: z.number().optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.customBrandName]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.questionText]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.ratingText]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.positiveReaction]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativeReaction]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.thanksToNegative]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.thanksToPositive]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.thresholdOfPositivity]: z.number(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.emailsForNegative]: z.string().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.notificationSettings]: z
        .object({
            [ReviewGeneratorQuestionnaireFormFieldsEnum.telegramForNegative]: z.boolean(),
            [ReviewGeneratorQuestionnaireFormFieldsEnum.tgChatToken]: z.string().nullable(),
        })
        .optional()
        .nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.termsAndConditionsLanguage]: z.string().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.positiveRatingButtonName]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativeRatingButtonName]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.customRatingButtonName]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.ratingScaleType]: z.nativeEnum(ReviewGeneratorRatingScaleTypeEnum),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativeReactionFormSettings]: z.object({
        name: z.object({
            show: z.boolean(),
            isRequired: z.boolean(),
            label: z.string(),
            placeholder: z.string(),
        }),
        email: z.object({
            show: z.boolean(),
            isRequired: z.boolean(),
            label: z.string(),
        }),
        phone: z.object({
            show: z.boolean(),
            isRequired: z.boolean(),
            label: z.string(),
        }),
        text: z.object({
            label: z.string(),
            placeholder: z.string(),
        }),
        submitButton: z.object({
            label: z.string(),
        }),
        skipButton: z.object({
            label: z.string(),
        }),
        reviewButton: z.object({
            label: z.string(),
        }),
    }),

    [ReviewGeneratorQuestionnaireFormFieldsEnum.canSendPromoCodeOnReview]: z.boolean().optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoCodeId]: z.number().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoReviewType]: z
        .nativeEnum(ReviewGeneratorPromoCodeTypeEnum)
        .nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoText]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativePromoText]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoTextThanks]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativePromoTextThanks]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoCodeWithProcessing]: z.boolean().default(false),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoCodeWithProcessingNegative]: z.boolean().default(false),

    [ReviewGeneratorQuestionnaireFormFieldsEnum.logo]: z.string().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.brandTemplateSettings]: z.number().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.color]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.shouldSaveTemplateSettings]: z.boolean().optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.mailingSubscriptionSettings]: z.object({
        ratingPageText: z.string(),
        ratingPageUnsubscribeLinkText: z.string(),

        subscribeTitle: z.string(),
        subscribeText: z.string(),

        returnToSurveyButtonText: z.string(),

        subscribeAgainText: z.string(),
        subscribeAgainButtonText: z.string(),

        unsubscribeTitle: z.string(),
        unsubscribeText: z.string(),
    }),
    code: z.string().nullable().optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.directRedirect]: z.boolean().optional(),

    [ReviewGeneratorQuestionnaireFormFieldsEnum.skipQuestionnaireRating]: z.boolean(),

    [ReviewGeneratorQuestionnaireFormFieldsEnum.catalogs]: z.array(sourceItemSchema).optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativeCatalogs]: z.array(sourceItemSchema).optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativeReviewMode]: z
        .union([z.literal('form'), z.literal('catalogs')])
        .optional(),

    [ReviewGeneratorQuestionnaireFormFieldsEnum.logoHeight]: z.number(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.displayAddress]: z.boolean(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.multiSurveyTitle]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.multiSurveySearchPlaceholder]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.multiSurveyButton]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.multiSurveyCompanyNameDisplay]: z.nativeEnum(CompanyNameStyleEnum),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.autoTagOption]: z.boolean(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.multiSurveyCompanyCommentDisplay]: z.boolean(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.buttonTextColor]: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.allowSocialMediaLinks]: z.boolean().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.socialsIds]: z.array(z.number()).nullish(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.socialMediaCallToAction]: z.string(),
    type: z.union([z.literal('rating'), z.literal('custom')]),
    customFormSettings: z.unknown(),
});

export type ReviewGeneratorQuestionnaireFormType = z.infer<typeof reviewGeneratorQuestionnaireFormSchema>;

export const questionnaireRetrieveSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.catalogs]: z.array(sourceItemSchema).optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.negativeCatalogs]: z.array(sourceItemSchema).optional(),
    directRedirect: z.boolean(),

    [ReviewGeneratorQuestionnaireFormFieldsEnum.skipQuestionnaireRating]: z.boolean(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoReviewType]: z
        .nativeEnum(ReviewGeneratorPromoCodeTypeEnum)
        .nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.promoCodeId]: z.number().nullable(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.ratingScaleType]: z.nativeEnum(ReviewGeneratorRatingScaleTypeEnum),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.brand]: z.number().optional(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.socialsIds]: z.array(z.number()).nullish(),
    [ReviewGeneratorQuestionnaireFormFieldsEnum.socialMediaCallToAction]: z.string(),
    type: z.union([z.literal('rating'), z.literal('custom')]),
    customFormSettings: z.unknown(),
});

export type ReviewGeneratorRetrieveQuestionnaireType = z.infer<typeof questionnaireRetrieveSchema>;

export const questionnaireBrandTemplateSettingsSchema = z.object({
    buttonTextColor: z.string(),
    color: z.string(),
    id: z.number(),
    logo: z.string().nullable(),
    logoHeight: z.number(),
});

export const uploadQuestionnaireLogoResponseSchema = z.object({
    logo: z.string(),
});

export type UploadQuestionnaireLogoResponseType = z.infer<typeof uploadQuestionnaireLogoResponseSchema>;

export type QuestionnaireBrandTemplateSettingsType = z.infer<typeof questionnaireBrandTemplateSettingsSchema>;

export const uploadLinkImageResponseSchema = z.object({
    id: z.string(),
    url: z.string(),
});

export type UploadQuestionnaireChangeLinkLogoUrlType = z.infer<typeof uploadLinkImageResponseSchema>;
